/**
 * SCENARIO REPORT
 *
 * Execution report view for a specific test scenario.
 * Displays summary metrics, status distribution, and per-run breakdown.
 */

import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { token } from '@atlaskit/tokens'
import Spinner from '@atlaskit/spinner'
import Lozenge from '@atlaskit/lozenge'

import StatusPieChart from './charts/StatusPieChart'
import PassRateGauge from './charts/PassRateGauge'
import StatusProgressBar from './charts/StatusProgressBar'
import MetricCardGrid, { createMetricsFromSummary, formatLastExecutionDate } from './cards/MetricCardGrid'
import ReportTabs from './ReportTabs'
import TrendsTab from './tabs/TrendsTab'
import DefectsTab from './tabs/DefectsTab'
import HistoryTab from './tabs/HistoryTab'
import {
  fetchScenarioReportAsync,
  selectScenarioReport,
  selectCurrentReportTab,
  selectReportLoading,
  selectReportErrors,
  openDrillDown
} from '../../store/slices/reportsSlice'

/**
 * Priority to Lozenge appearance mapping
 */
const PRIORITY_APPEARANCE = {
  'Critical': 'removed',
  'High': 'moved',
  'Medium': 'new',
  'Low': 'default'
}

/**
 * ScenarioReport Component
 *
 * @param {object} props
 * @param {number|string} props.scenarioId - Test scenario ID
 */
const ScenarioReport = ({ scenarioId }) => {
  const dispatch = useDispatch()

  // Selectors
  const report = useSelector(selectScenarioReport)
  const currentTab = useSelector(selectCurrentReportTab)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  // Fetch data whenever the scenario changes.
  useEffect(() => {
    if (scenarioId) {
      dispatch(fetchScenarioReportAsync({ scenarioId, includeRunBreakdown: true }))
    }
  }, [dispatch, scenarioId])

  // Until the scenario-level data lands we can't show the header or tabs
  // meaningfully — fall back to a full-panel spinner / error / empty.
  if (!report) {
    if (loading.scenario) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px'
        }}>
          <Spinner size="large" />
        </div>
      )
    }
    if (errors.scenario) {
      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: token('color.text.danger', '#DE350B')
        }}>
          <p>Failed to load scenario report</p>
          <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>
            {errors.scenario}
          </p>
        </div>
      )
    }
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        color: token('color.text.subtle', '#6B778C')
      }}>
        No report data available
      </div>
    )
  }

  const { summary, runBreakdown } = report
  const metrics = createMetricsFromSummary(summary)

  return (
    <div style={{ padding: '0' }}>
      {/* Header info — always visible so the user keeps scope context while switching tabs. */}
      <div style={{
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '16px',
          fontWeight: 600,
          color: token('color.text', '#172B4D')
        }}>
          {report.scenarioName}
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginTop: '8px',
          fontSize: '13px',
          color: token('color.text.subtle', '#6B778C')
        }}>
          {report.releaseName && (
            <span>Release: {report.releaseName}</span>
          )}
          {report.priority && (
            <Lozenge appearance={PRIORITY_APPEARANCE[report.priority] || 'default'}>
              {report.priority}
            </Lozenge>
          )}
        </div>
      </div>

      <ReportTabs level="scenario" />

      {currentTab === 'trends' && (
        <TrendsTab level="scenario" id={scenarioId} />
      )}

      {currentTab === 'defects' && (
        <DefectsTab level="scenario" id={scenarioId} />
      )}

      {currentTab === 'history' && (
        <HistoryTab scenarioId={scenarioId} />
      )}

      {currentTab === 'overview' && (
        <>
      {/* Charts row */}
      <div style={{
        display: 'flex',
        gap: '32px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        {/* Status distribution chart — clicking a segment opens the drill-down panel. */}
        <div style={{ flex: '1 1 280px', minWidth: '280px', maxWidth: '350px' }}>
          <StatusPieChart
            statusDistribution={summary.statusDistribution}
            height={260}
            centerLabel="Executions"
            onSegmentClick={(statusName) =>
              dispatch(openDrillDown({ level: 'scenario', id: scenarioId, status: [statusName] }))
            }
          />
        </div>

        {/* Pass rate gauge */}
        <div style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <PassRateGauge
            passRate={summary.passRate}
            size={180}
            showStatus={true}
          />
        </div>
      </div>

      {/* Metric cards */}
      <div style={{ marginBottom: '24px' }}>
        <MetricCardGrid metrics={metrics} compact={true} gap="12px" />
      </div>

      {/* Last execution info */}
      <div style={{
        marginBottom: '24px',
        fontSize: '13px',
        color: token('color.text.subtle', '#6B778C')
      }}>
        Last Execution: {formatLastExecutionDate(summary.lastExecutionDate)}
      </div>

      {/* Run breakdown table */}
      {runBreakdown && runBreakdown.length > 0 && (
        <div>
          <h4 style={{
            margin: '0 0 12px 0',
            fontSize: '14px',
            fontWeight: 600,
            color: token('color.text', '#172B4D')
          }}>
            Test Runs Breakdown
          </h4>

          <div style={{
            border: `1px solid ${token('color.border', '#DFE1E6')}`,
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: token('color.background.neutral', '#F4F5F7')
                }}>
                  <th style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: token('color.text.subtle', '#6B778C'),
                    borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
                  }}>
                    Run
                  </th>
                  <th style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: token('color.text.subtle', '#6B778C'),
                    borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
                  }}>
                    Status
                  </th>
                  <th style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: token('color.text.subtle', '#6B778C'),
                    borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`,
                    width: '200px'
                  }}>
                    Progress
                  </th>
                  <th style={{
                    padding: '10px 12px',
                    textAlign: 'right',
                    fontWeight: 500,
                    color: token('color.text.subtle', '#6B778C'),
                    borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
                  }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {runBreakdown.map((run, index) => (
                  <tr
                    key={run.runId}
                    style={{
                      backgroundColor: index % 2 === 0
                        ? token('elevation.surface', '#FFFFFF')
                        : token('color.background.neutral.subtle', '#FAFBFC')
                    }}
                  >
                    <td style={{
                      padding: '10px 12px',
                      color: token('color.text', '#172B4D'),
                      borderBottom: index < runBreakdown.length - 1
                        ? `1px solid ${token('color.border', '#DFE1E6')}`
                        : 'none'
                    }}>
                      {/*
                        Runs are rendered in creation order (backend returns them sorted
                        by id ASC). Match the zero-padded ordinal labels used on the
                        scenario detail page (01, 02, 03 …) instead of the raw DB id.
                      */}
                      Run {String(index + 1).padStart(2, '0')}
                    </td>
                    <td style={{
                      padding: '10px 12px',
                      borderBottom: index < runBreakdown.length - 1
                        ? `1px solid ${token('color.border', '#DFE1E6')}`
                        : 'none'
                    }}>
                      <Lozenge appearance={run.isCompleted ? 'success' : 'inprogress'}>
                        {run.isCompleted ? 'Completed' : 'Active'}
                      </Lozenge>
                    </td>
                    <td style={{
                      padding: '10px 12px',
                      borderBottom: index < runBreakdown.length - 1
                        ? `1px solid ${token('color.border', '#DFE1E6')}`
                        : 'none'
                    }}>
                      <StatusProgressBar
                        statusDistribution={run.statusCounts}
                        height={8}
                      />
                    </td>
                    <td style={{
                      padding: '10px 12px',
                      textAlign: 'right',
                      color: token('color.text.subtle', '#6B778C'),
                      borderBottom: index < runBreakdown.length - 1
                        ? `1px solid ${token('color.border', '#DFE1E6')}`
                        : 'none'
                    }}>
                      {run.totalExecutions}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  )
}

export default ScenarioReport
